import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { siteContentAPI } from '../lib/api';
import { usePageMeta } from '../components/shared/SEO';
import { Toaster } from '../components/ui/sonner';
import TaxCalculatorDialog from '../components/calculator/TaxCalculatorDialog';
import { PesquisaForm } from '../components/pesquisa/PesquisaForm';
import { PesquisaResultado } from '../components/pesquisa/PesquisaResultado';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hub-medico-apex/artifacts/zsn6307r_Logotipo%20com%20tag.png';
const SYMBOL_URL = 'https://customer-assets.emergentagent.com/job_hub-medico-apex/artifacts/ft6xenx5_Simbolo%20Apex.png';

const PesquisaPlantaoPage = () => {
  const [texts, setTexts] = useState({});
  const [resultado, setResultado] = useState(null);
  const [calcOpen, setCalcOpen] = useState(false);

  useEffect(() => {
    siteContentAPI.getAll().then((r) => setTexts(r.data || {})).catch(() => {});
  }, []);

  const t = (key, fallback) => texts[key] || fallback;

  usePageMeta({
    title: `${t('pesquisa-title', 'Quanto vale o plantão do cirurgião pediátrico')} | Apex Medical Hub`,
    description: 'Pesquisa anônima, 8 perguntas, 2 minutos. Veja como o seu plantão se compara com o dos colegas.',
    canonical: '/pesquisa-plantao',
    image: '/og/plantao.jpg',
  });

  const handleEnviado = (data) => {
    setResultado(data);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-[#f3f7fa] flex flex-col" data-testid="pesquisa-plantao-page">
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-[680px] px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="pesquisa-logo-link">
            <img src={LOGO_URL} alt="Apex Medical Hub" className="h-9 sm:h-10 w-auto" />
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-secondary bg-secondary-50 border border-secondary-100 rounded-full px-3 py-1">
            <ShieldCheck className="w-3.5 h-3.5" />
            Dados anônimos
          </span>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-[680px] px-5 py-8 pb-16">
          <div className="mb-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-secondary" data-testid="pesquisa-badge">
              {t('pesquisa-badge', 'Pesquisa anônima')}
            </p>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl leading-[1.1] font-bold tracking-tight text-primary" data-testid="pesquisa-title">
              {t('pesquisa-title', 'Quanto vale o plantão do cirurgião pediátrico')}
            </h1>
            <p className="mt-3 text-[15px] text-slate-600 max-w-[52ch]" data-testid="pesquisa-subtitle">
              {t('pesquisa-subtitle', 'Oito perguntas rápidas sobre o plantão que você faz hoje. No fim você vê como o seu valor se compara com o dos colegas e pode calcular quanto de imposto está saindo do seu bolso.')}
            </p>
            <div className="mt-4 flex gap-3 rounded-[10px] px-3.5 py-3 text-[13.5px] leading-snug bg-secondary-50 border border-secondary-100 text-slate-700" data-testid="pesquisa-privacy-note">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-none text-secondary" />
              <span>{t('pesquisa-privacy', 'Nome e contato são opcionais e ficam separados dos dados da pesquisa. Sem eles, nada identifica quem respondeu.')}</span>
            </div>
          </div>

          {!resultado ? (
            <PesquisaForm onEnviado={handleEnviado} />
          ) : (
            <PesquisaResultado {...resultado} t={t} onAbrirCalculadora={() => setCalcOpen(true)} />
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-[680px] px-5 py-5 flex flex-wrap items-center justify-between gap-3 text-[12.5px] text-slate-400">
          <span className="inline-flex items-center gap-2">
            <img src={SYMBOL_URL} alt="" className="h-5 w-5" />
            Apex Medical Hub · pesquisa independente, uso estatístico
          </span>
          <Link to="/" className="hover:text-primary transition-colors" data-testid="pesquisa-footer-site-link">apexmedicalhub.com</Link>
        </div>
      </footer>

      <TaxCalculatorDialog open={calcOpen} onOpenChange={setCalcOpen} />
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default PesquisaPlantaoPage;
